// Centralized multi-currency pricing.
// Base prices are always in RON (Romanian Leu). All other currencies are
// derived from a central conversion rate, with an option to override each
// price with a clean, tourism-friendly rounded value ("commercial price").
//
// To change the international displayed prices in the future, edit either:
//   1. `RATES` for a global re-calculation, OR
//   2. `PRICE_OVERRIDES` for a specific tour/rental key.

export const CURRENCY_BY_LANG = {
  ro: "RON",
  en: "EUR",
  de: "EUR",
  hu: "HUF",
};

// Conversion rates from RON. Edit here to shift ALL derived prices at once.
export const RATES = {
  RON: 1,
  EUR: 0.20,  // ~1 RON ≈ 0.20 EUR
  HUF: 78,    // ~1 RON ≈ 78 HUF
};

// Rounding rules per currency — produces tourism-friendly clean prices.
// RON keeps the base exactly. EUR rounds to nearest 5. HUF rounds to nearest 500.
const ROUND = {
  RON: (v) => Math.round(v),
  EUR: (v) => Math.round(v / 5) * 5,
  HUF: (v) => Math.round(v / 500) * 500,
};

// Optional explicit commercial prices per key (tour slug / rental key) per currency.
// If provided, these override the computed value.
// Use this to lock a nice round number (e.g. €90) instead of the raw conversion.
export const PRICE_OVERRIDES = {
  // Oradea tours (base 300 RON)
  "sunset-panorama-ride":         { EUR: 60,  HUF: 24000 },
  "northern-forest-escape":       { EUR: 60,  HUF: 24000 },
  "lake-forest-explorer":         { EUR: 60,  HUF: 24000 },
  // Apuseni multi-day
  "apuseni-day-escape":           { EUR: 90,  HUF: 35000 },
  "apuseni-weekend-escape":       { EUR: 400, HUF: 155000 },
  "transylvania-ebike-expedition":{ EUR: 1000, HUF: 390000 },
  // Rentals
  "rental-full-day":              { EUR: 60,  HUF: 24000 },
  "rental-short-ride":            { EUR: 36,  HUF: 14000 },
  // Optional local experiences (base 75 RON)
  "local-stana":                  { EUR: 15,  HUF: 6000 },
};

// Format a number as a currency-labeled string.
function formatWithCurrency(amount, currency, lang) {
  if (currency === "HUF") {
    return `${amount.toLocaleString(lang === "hu" ? "hu-HU" : "en-US")} Ft`;
  }
  if (currency === "EUR") return `€${amount}`;
  return `${amount} lei`;
}

// Compute the display amount for a given RON base + language + optional slug key.
export function priceInLang(ronPrice, lang, key = null) {
  const currency = CURRENCY_BY_LANG[lang] || "RON";
  if (currency === "RON") {
    return { amount: ROUND.RON(ronPrice), currency, formatted: formatWithCurrency(ROUND.RON(ronPrice), currency, lang) };
  }
  const override = key && PRICE_OVERRIDES[key] && PRICE_OVERRIDES[key][currency];
  const amount = override != null ? override : ROUND[currency](ronPrice * RATES[currency]);
  return { amount, currency, formatted: formatWithCurrency(amount, currency, lang) };
}

// Convenience: just the formatted string.
export function formatPrice(ronPrice, lang, key = null) {
  return priceInLang(ronPrice, lang, key).formatted;
}

// Whether to show a small "prices are indicative" disclaimer.
export function shouldShowCurrencyNote(lang) {
  return CURRENCY_BY_LANG[lang] !== "RON";
}
